import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { alignClass } from "../lib/align";
import { Reveal, DynamicIcon, Button, SmartImage } from "../components/ui";
import ProjectCard from "../components/ProjectCard";
import LeadForm from "../components/LeadForm";
import SEO from "../components/SEO";
import { CheckCircle2, Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setService(null);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setService(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (service === false) return <div className="pt-40 pb-24 text-center container-x"><h1 className="text-2xl font-bold">Service not found</h1><Button to="/services" className="mt-6">Back to Services</Button></div>;
  if (!service) return <div className="pt-40 pb-24 text-center text-muted">Loading...</div>;

  return (
    <>
      <SEO title={service.seo_title || service.name} description={service.seo_description || service.short_description} image={service.cover_image}
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", name: service.name, description: service.short_description, areaServed: "Indore" }} />

      <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 text-white overflow-hidden">
        <img src={service.cover_image} alt={service.name} className="absolute inset-0 img-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="container-x relative">
          <div className="kicker mb-3">{service.category}</div>
          <h1 className="text-4xl md:text-6xl font-extrabold max-w-3xl">{service.name}</h1>
          <p className="mt-4 text-white/70 max-w-xl text-lg">{service.short_description}</p>
          <Button to="/contact" variant="primary" className="mt-8">Get Free Consultation</Button>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container-x grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <Reveal>
              <h2 className="text-2xl font-bold text-ink mb-4">Overview</h2>
              <p className={`text-muted leading-relaxed ${alignClass(service.full_description_align)}`}>{service.full_description}</p>
            </Reveal>

            {service.benefits?.length > 0 && (
              <Reveal>
                <h2 className="text-2xl font-bold text-ink mb-5">Benefits</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {service.benefits.map((b, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-line p-4 bg-sand">
                      <CheckCircle2 className="text-gold shrink-0 mt-0.5" size={18} /><span className="text-sm text-ink">{b}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {service.process?.length > 0 && (
              <Reveal>
                <h2 className="text-2xl font-bold text-ink mb-5">Our Process</h2>
                <div className="space-y-3">
                  {service.process.map((p, i) => (
                    <div key={i} className="flex items-center gap-4 rounded-xl border border-line p-4">
                      <span className="w-9 h-9 rounded-full bg-ink text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="font-medium text-ink">{p}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {service.faqs?.length > 0 && (
              <Reveal>
                <h2 className="text-2xl font-bold text-ink mb-5">FAQ</h2>
                <div className="divide-y divide-line border-y border-line">
                  {service.faqs.map((f, i) => (
                    <div key={i}>
                      <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex justify-between gap-4 py-4 text-left font-semibold text-ink">
                        {f.q}{openFaq === i ? <Minus size={18} className="text-gold" /> : <Plus size={18} />}
                      </button>
                      <AnimatePresence>{openFaq === i && <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-4 text-muted">{f.a}</motion.p>}</AnimatePresence>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-xl2 border border-line bg-sand p-6">
              <h3 className="text-lg font-bold text-ink mb-4">Enquire About {service.name}</h3>
              <LeadForm sourcePage={`Service: ${service.name}`} defaultService={service.category} />
            </div>
          </div>
        </div>
      </section>

      {service.related_projects?.length > 0 && (
        <section className="py-16 md:py-20 bg-sand">
          <div className="container-x">
            <h2 className="text-2xl font-bold text-ink mb-8">Related Projects</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {service.related_projects.map((p) => <ProjectCard key={p.id} project={p} onOpen={() => {}} aspect="aspect-[4/5]" />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
